import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useElementSize } from '../lib/useElementSize';
import { formatDecimal } from './palette';

/**
 * Grafica della dashboard: anelli, tachimetro, sparkline.
 *
 * Porting 1:1 delle funzioni SVG dell'Allegato A (`ringSVG`, `solidRingSVG`,
 * `fadedRingSVG`, `riskGaugeSVG`, `sparklineSVG`). Geometrie, colori, spessori,
 * opacità e arrotondamenti sono quelli del riferimento: il collaudo verifica la
 * conformità visiva, quindi qui si replica, non si migliora.
 */

const RING_COLORS: Record<string, string> = {
  red: '#A32D2D',
  orange: '#D85A30',
  amber: '#EF9F27',
  green: '#2A9D8F',
  gray: '#9B9590',
  navy: '#142E4F',
};

function resolveColor(color: string): string {
  return color.startsWith('#') ? color : (RING_COLORS[color] ?? '#9B9590');
}

interface RingProps {
  value: number | null;
  max: number;
  color: string;
  size: number;
  stroke?: number;
  gradient?: boolean;
  /** Necessario per gli id dei gradient: due anelli non devono collidere. */
  gradientId?: string;
}

/**
 * [YBD] Il `ringSVG` del riferimento accetta un'opzione `dashed`, ma non ha mai
 * effetto: la funzione scrive `stroke-dasharray` due volte sullo stesso
 * elemento (una col valore della circonferenza, una con "3 4") e il parser HTML
 * tiene il primo attributo. Verificato sul DOM di Chrome sull'Allegato A.
 *
 * Conseguenza sul rendering reale: l'anello "tratteggiato" del FameIndex N.D.
 * ha dasharray e dashoffset entrambi pari alla circonferenza, quindi non
 * disegna nulla e si vede solo la traccia grigia. Qui si replica quel risultato
 * e l'opzione non esiste: implementarla davvero produrrebbe un cerchio
 * tratteggiato visibile, cioè uno scostamento dall'HTML congelato.
 */
export function Ring({
  value,
  max,
  color,
  size,
  stroke = 6,
  gradient = false,
  gradientId = 'ring',
}: RingProps): ReactNode {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const pct = value != null ? Math.min(value / max, 1) : 0;
  const offset = circ * (1 - pct);
  const c = resolveColor(color);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {gradient ? (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity="0.85" />
            <stop offset="100%" stopColor={c} stopOpacity="1" />
          </linearGradient>
        </defs>
      ) : null}
      <circle
        className="ring-track"
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#EDEDEE"
        strokeWidth={stroke}
      />
      <circle
        className="ring-fill"
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={gradient ? `url(#${gradientId})` : c}
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Anello pieno con gradiente leggero: FameIndex e FameIntegrity. */
export function SolidRing(props: Omit<RingProps, 'stroke' | 'gradient'>): ReactNode {
  return <Ring {...props} stroke={7} gradient />;
}

/**
 * Anello tratteggiato e attenuato: il WebScore è l'apparenza, e il riferimento
 * lo rende visivamente meno solido degli altri indici.
 */
export function FadedRing({
  value,
  max,
  size,
}: {
  value: number;
  max: number;
  size: number;
}): ReactNode {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value / max, 1);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        className="ring-track"
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#EDEDEE"
        strokeWidth={6}
      />
      <circle
        className="ring-fill"
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#9B9590"
        strokeWidth={6}
        strokeLinecap="round"
        style={{ strokeDasharray: `${circ * pct}px ${circ}px`, opacity: 0.65 }}
      />
    </svg>
  );
}

const RISK_SEGMENT_COLORS = ['#2A9D8F', '#7FB55E', '#EF9F27', '#D85A30', '#A32D2D'];

/** Tachimetro semicircolare del FameRisk: 5 segmenti più lancetta. */
export function RiskGauge({ level }: { level: number }): ReactNode {
  const cx = 65;
  const cy = 65;
  const r = 50;
  const startA = Math.PI;
  const totalA = Math.PI;
  const gap = 0.018;

  const segments = Array.from({ length: 5 }, (_, i) => {
    const a1 = startA + (i / 5) * totalA;
    const a2 = startA + ((i + 1) / 5) * totalA;
    const x1 = cx + r * Math.cos(a1 + gap);
    const y1 = cy + r * Math.sin(a1 + gap);
    const x2 = cx + r * Math.cos(a2 - gap);
    const y2 = cy + r * Math.sin(a2 - gap);
    const isActive = i + 1 === level;
    const aMid = (a1 + a2) / 2;

    return {
      i,
      d: `M${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 0,1 ${x2.toFixed(2)},${y2.toFixed(2)}`,
      opacity: isActive ? 1 : 0.22,
      isActive,
      tx: cx + (r + 12) * Math.cos(aMid),
      ty: cy + (r + 12) * Math.sin(aMid) + 3,
    };
  });

  const needleA = startA + ((level - 0.5) / 5) * totalA;
  const nx = cx + (r - 4) * Math.cos(needleA);
  const ny = cy + (r - 4) * Math.sin(needleA);
  const needleColor = RISK_SEGMENT_COLORS[level - 1] ?? '#A32D2D';
  const bw = 3.5;
  const bx1 = cx + bw * Math.cos(needleA + Math.PI / 2);
  const by1 = cy + bw * Math.sin(needleA + Math.PI / 2);
  const bx2 = cx + bw * Math.cos(needleA - Math.PI / 2);
  const by2 = cy + bw * Math.sin(needleA - Math.PI / 2);

  return (
    <svg viewBox="0 0 130 78" preserveAspectRatio="xMidYMid meet">
      {segments.map((s) => (
        <path
          key={`arc-${s.i}`}
          d={s.d}
          fill="none"
          stroke={RISK_SEGMENT_COLORS[s.i]}
          strokeWidth={11}
          strokeLinecap="butt"
          opacity={s.opacity}
        />
      ))}
      {segments.map((s) => (
        <text
          key={`tick-${s.i}`}
          x={s.tx.toFixed(1)}
          y={s.ty.toFixed(1)}
          fontFamily="IBM Plex Mono,monospace"
          fontSize="9"
          fill={RISK_SEGMENT_COLORS[s.i]}
          opacity={s.isActive ? 1 : 0.45}
          textAnchor="middle"
          fontWeight={s.isActive ? 600 : 400}
        >
          {s.i + 1}
        </text>
      ))}
      <g
        className="gauge-needle"
        style={{ transform: 'rotate(0deg)', transformOrigin: `${cx}px ${cy}px` }}
      >
        <polygon
          points={`${bx1.toFixed(2)},${by1.toFixed(2)} ${bx2.toFixed(2)},${by2.toFixed(2)} ${nx.toFixed(2)},${ny.toFixed(2)}`}
          fill={needleColor}
        />
        <circle cx={cx} cy={cy} r={5} fill="#142E4F" />
        <circle cx={cx} cy={cy} r={2.5} fill={needleColor} />
      </g>
    </svg>
  );
}

interface SparklineProps {
  values: (number | null)[];
  color: string;
  fillColor: string;
  months: string[];
  /** Decimali delle due etichette di valore; il riferimento assume 2 di default. */
  decimals?: number | undefined;
}

/**
 * Sparkline degli andamenti, con griglia verticale, etichette dei periodi e —
 * dal v40 — una banda superiore alta `hVal` con il valore iniziale e finale
 * della serie.
 *
 * La virgola decimale è quella del riferimento, non dipende dalla lingua: è la
 * stessa scelta già in uso per gli importi (`palette.ts`) e i conteggi di
 * recensioni (`PlatformCards.tsx`).
 *
 * Unica divergenza dichiarata: il viewBox è in pixel misurati e non in unità
 * fisse 200x58, per non deformare il testo. Vedi il commento `[YBD]` nel corpo.
 */
export function Sparkline({
  values,
  color,
  fillColor,
  months,
  decimals,
}: SparklineProps): ReactNode {
  // [YBD] Il riferimento usa preserveAspectRatio="none" su un viewBox 200x58
  // in un box di proporzioni diverse, e stira anche i testi. Qui il viewBox
  // e' in pixel reali (scala 1:1): stesse geometrie, riscalate, testo intatto.
  const refW = 200;
  const refH = 58;
  const [svg, box] = useElementSize<SVGSVGElement>({ width: refW, height: refH });

  const clean = values.filter((v): v is number => v !== null);
  if (clean.length !== values.length || values.length < 2) return null;

  const w = box.width;
  const h = box.height;
  const sx = w / refW;
  const sy = h / refH;

  const hVal = 12 * sy;
  const hChart = 32 * sy;
  const padX = 2 * sx;
  const padY = 2 * sy;

  const min = Math.min(...clean) * 0.98;
  const max = Math.max(...clean) * 1.02;
  const range = max - min || 1;

  const points = clean.map((v, i) => {
    const x = padX + (i / (clean.length - 1)) * (w - padX * 2);
    const y = hVal + padY + (1 - (v - min) / range) * (hChart - padY * 2);
    return { x, y, month: months[i] };
  });

  const last = points[points.length - 1];
  const first = clean[0];
  const lastValue = clean[clean.length - 1];
  if (!last || first === undefined || lastValue === undefined) return null;

  const line = points.map((p) => `${p.x},${p.y}`).join(' ');
  const baseline = hVal + hChart;
  const fillPoints = `${padX},${baseline} ${line} ${last.x},${baseline}`;

  const dec = decimals ?? 2;
  const fmt = (n: number): string => formatDecimal(n, dec);

  return (
    <svg ref={svg} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {points.map((p, i) => (
        <line
          key={`g-${i}`}
          x1={p.x}
          y1={hVal}
          x2={p.x}
          y2={baseline}
          stroke="#E5E5E6"
          strokeWidth={0.5}
        />
      ))}
      <text
        x={w - padX}
        y={hVal - 3 * sy}
        fontFamily="IBM Plex Mono,monospace"
        fontSize="8"
        fill="#6B6560"
        textAnchor="end"
      >
        {fmt(lastValue)}
      </text>
      {/* Il valore iniziale compare solo se la serie si e' mossa: su una serie
          piatta il riferimento non lo stampa, per non ripetere lo stesso numero. */}
      {Math.abs(first - lastValue) > 0.0001 ? (
        <text
          x={padX}
          y={hVal - 3 * sy}
          fontFamily="IBM Plex Mono,monospace"
          fontSize="8"
          fill="#9A948C"
          textAnchor="start"
        >
          {fmt(first)}
        </text>
      ) : null}
      <line x1={0} y1={baseline} x2={w} y2={baseline} stroke="#E5E5E6" strokeWidth={0.5} />
      <polygon points={fillPoints} fill={fillColor} opacity={0.1} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last.x} cy={last.y} r={2.5} fill={color} />
      {points.map((p, i) => {
        if (!p.month) return null;
        // Prima e ultima etichetta ancorate ai bordi, cosi' non sbordano dal
        // viewBox: nel v39 erano centrate sul punto e venivano tagliate.
        const isLast = i === points.length - 1;
        const anchor = i === 0 ? 'start' : isLast ? 'end' : 'middle';
        const x = i === 0 ? padX : isLast ? w - padX : p.x;
        return (
          <text
            key={`m-${i}`}
            x={x}
            y={h - 2 * sy}
            fontFamily="IBM Plex Mono,monospace"
            fontSize="7"
            fill="#142E4F"
            textAnchor={anchor}
            letterSpacing="0.5"
          >
            {p.month}
          </text>
        );
      })}
    </svg>
  );
}

export type TrendDirection = 'up' | 'down' | 'flat';

export function TrendArrow({ direction }: { direction: TrendDirection }): ReactNode {
  const d =
    direction === 'up'
      ? 'M12 19V5M8 9l4-4 4 4'
      : direction === 'down'
        ? 'M12 5v14M8 15l4 4 4-4'
        : 'M5 12h14';
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d={d} />
    </svg>
  );
}

/**
 * Variazione fra primo e ultimo snapshot, con la stessa soglia del riferimento.
 *
 * `val` è null quando la serie è stabile: la parola corrispondente è testo di
 * interfaccia e va tradotta da chi rende la card, non decisa qui.
 */
export function trendDelta(
  values: (number | null)[] | null,
  decimals = 2,
): { dir: TrendDirection; val: string | null } {
  if (!values || values.length < 2) return { dir: 'flat', val: '—' };
  const first = values[0];
  const last = values[values.length - 1];
  if (first == null || last == null) return { dir: 'flat', val: '—' };

  const diff = last - first;
  if (Math.abs(diff) < 0.005) return { dir: 'flat', val: null };
  // Virgola come nel resto del Level 1 (16/10, punto 7): il segno resta davanti.
  return {
    dir: diff > 0 ? 'up' : 'down',
    val: `${diff > 0 ? '+' : ''}${formatDecimal(diff, decimals)}`,
  };
}

/**
 * Etichetta della card, con la "i" e il tooltip introdotti dal v40 quando c'è
 * un testo esplicativo da mostrare.
 */
function TrendLabel({ label, info }: { label: string; info?: ReactNode }): ReactNode {
  if (!info) return <span className="trend-card-label">{label}</span>;
  return (
    <span className="trend-label-wrap">
      <span className="trend-card-label">{label}</span>
      <span className="trend-info">i</span>
      <span className="trend-tooltip">{info}</span>
    </span>
  );
}

interface TrendCardProps {
  label: string;
  value: string;
  unit?: string;
  values: (number | null)[] | null;
  color: string;
  decimals?: number;
  months: string[];
  /**
   * Corpo del valore ridotto, come fa il riferimento su percentuali e volumi.
   * È un flag esplicito e non un confronto sull'etichetta: quella è tradotta,
   * e legarci la grafica la romperebbe al primo cambio di lingua.
   */
  compact?: boolean;
  /** Testo del tooltip. Assente sulle card che nel riferimento non ce l'hanno. */
  info?: ReactNode;
}

export function TrendCard({
  label,
  value,
  unit = '',
  values,
  color,
  decimals,
  months,
  compact = false,
  info,
}: TrendCardProps): ReactNode {
  const { t } = useTranslation();
  const delta = trendDelta(values, decimals);
  const small = unit === '%' || compact;

  return (
    <div className="trend-card">
      <div className="trend-card-header">
        <TrendLabel label={label} info={info} />
        <span className={`trend-card-delta ${delta.dir}`}>
          <TrendArrow direction={delta.dir} /> {delta.val ?? t('insight.stable')}
        </span>
      </div>
      <div className={small ? 'trend-card-value small' : 'trend-card-value'}>
        {value}
        {unit}
      </div>
      <div className="sparkline-wrap">
        {values ? (
          <Sparkline
            values={values}
            color={color}
            fillColor={color}
            months={months}
            decimals={decimals}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Card della prima rilevazione, introdotta dal v40: stessa testata della
 * `TrendCard` ma senza delta e senza sparkline, con al loro posto un
 * segnaposto tratteggiato. Si usa quando il brand ha un solo snapshot.
 */
export function FirstCard({
  label,
  value,
  unit = '',
  compact = false,
  info,
}: {
  label: string;
  value: string;
  unit?: string;
  compact?: boolean;
  info?: ReactNode;
}): ReactNode {
  const small = unit === '%' || compact;
  return (
    <div className="trend-card first">
      <div className="trend-card-header">
        <TrendLabel label={label} info={info} />
      </div>
      <div className={small ? 'trend-card-value small' : 'trend-card-value'}>
        {value}
        {unit}
      </div>
      <div className="first-placeholder" />
    </div>
  );
}
